import React, { ComponentType, CSSProperties, ReactNode, useCallback, useState } from "react";

import cn from "classnames";
import { DragDropContext, DragStart, DropResult } from "react-beautiful-dnd";

import Identified from "@mapmycustomers/shared/types/base/Identified";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import Column from "./components/Column";
import styles from "./DnDBoard.module.scss";
import BoardColumn, { BoardColumnId } from "./types/BoardColumn";
import ColumnFooterProps from "./types/ColumnFooterProps";
import ColumnHeaderProps from "./types/ColumnHeaderProps";
import ItemComponentProps from "./types/ItemComponentProps";

interface Props<ColumnDataType, ColumnItemType> {
  analyticIssuer?: AnalyticsService;
  className?: string;
  columnFooter?: ComponentType<ColumnFooterProps<ColumnDataType, ColumnItemType>>;
  columns: BoardColumn<ColumnDataType, ColumnItemType>[];
  columnSubHeader?: ComponentType<ColumnHeaderProps<ColumnDataType, ColumnItemType>>;
  extraLeft?: ReactNode;
  extraRight?: ReactNode;
  getColumnStyles?: (
    column: BoardColumn<ColumnDataType, ColumnItemType>,
    props: { dragging?: boolean; isDestination?: boolean; isSource?: boolean }
  ) => CSSProperties | undefined;
  getItemListStyles?: (
    column: BoardColumn<ColumnDataType, ColumnItemType>,
    props: { dragging?: boolean; isDestination?: boolean; isSource?: boolean }
  ) => CSSProperties | undefined;
  hiddenColumnIds: BoardColumnId[];
  itemComponent: ComponentType<ItemComponentProps<ColumnItemType>>;
  loading?: boolean;
  onChangeHiddenColumns: (ids: BoardColumnId[]) => void;
  onDragged?: (payload: {
    destination: BoardColumn<ColumnDataType, ColumnItemType>;
    item: ColumnItemType;
    source: BoardColumn<ColumnDataType, ColumnItemType>;
  }) => void;
  onLoadMore?: (data: ColumnDataType) => void;
}

const DnDBoard = <ColumnDataType, ColumnItemType extends Identified>({
  analyticIssuer,
  className,
  columnFooter,
  columns,
  columnSubHeader,
  extraLeft,
  extraRight,
  getColumnStyles,
  getItemListStyles,
  hiddenColumnIds,
  itemComponent,
  loading,
  onChangeHiddenColumns,
  onDragged,
  onLoadMore,
}: Props<ColumnDataType, ColumnItemType>) => {
  const handleShowColumn = useCallback(
    (column: BoardColumn<ColumnDataType, ColumnItemType>) => {
      onChangeHiddenColumns(hiddenColumnIds.filter((id) => id !== column.id));
    },
    [hiddenColumnIds, onChangeHiddenColumns]
  );
  const handleHideColumn = useCallback(
    (column: BoardColumn<ColumnDataType, ColumnItemType>) => {
      onChangeHiddenColumns([...hiddenColumnIds, column.id]);
      analyticIssuer?.clicked(["Hide this Column"], column);
    },
    [analyticIssuer, hiddenColumnIds, onChangeHiddenColumns]
  );

  const [sourceColumnId, setSourceColumnId] = useState<BoardColumnId | undefined>();
  const [hoveredColumnId, setHoveredColumnId] = useState<BoardColumnId | undefined>();
  const [draggedItemId, setDraggedItemId] = useState<ColumnItemType["id"] | undefined>();

  const handleDragStart = useCallback(
    (e: DragStart) => {
      setSourceColumnId(parseInt(e.source.droppableId, 10));
      setDraggedItemId(parseInt(e.draggableId, 10));
      analyticIssuer?.initiated(["Drag and Drop Deal"]);
    },
    [analyticIssuer]
  );

  const handleHoverColumn = useCallback(
    (column: BoardColumn<ColumnDataType, ColumnItemType>) => setHoveredColumnId(column.id),
    []
  );

  const onDragEnd = useCallback(
    ({ destination, draggableId, source }: DropResult) => {
      setSourceColumnId(undefined);
      setDraggedItemId(undefined);
      if (!destination || destination.droppableId === source.droppableId) {
        analyticIssuer?.cancelled(["Drag and Drop Deal", "Canceled"]);
        return;
      }

      const sourceColumnId = parseInt(source.droppableId, 10);
      const sourceColumn = columns.find(({ id }) => id === sourceColumnId);
      const destinationColumnId = parseInt(destination.droppableId, 10);
      const destinationColumn = columns.find(({ id }) => id === destinationColumnId);
      if (!sourceColumn || !destinationColumn) {
        return;
      }

      const itemId = parseInt(draggableId, 10);
      const draggedItem = sourceColumn.items.find(({ id }) => id === itemId);
      if (!draggedItem) {
        return;
      }

      onDragged?.({ destination: destinationColumn, item: draggedItem, source: sourceColumn });
      analyticIssuer?.completed(["Drag and Drop Deal", "Complete"]);
    },
    [analyticIssuer, columns, onDragged]
  );

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={handleDragStart}>
      <div className={cn(styles.container, className)}>
        <div className={styles.columns}>
          {extraLeft}
          {columns.map((column) => (
            <Column<ColumnDataType, ColumnItemType>
              analyticIssuer={analyticIssuer}
              column={column}
              columnFooter={columnFooter}
              columnSubHeader={columnSubHeader}
              dragDestination={column.id === hoveredColumnId}
              draggingInProgress={!!sourceColumnId}
              draggingItemId={column.id === sourceColumnId ? draggedItemId : undefined}
              dragSource={column.id === sourceColumnId}
              getColumnStyles={getColumnStyles}
              getItemListStyles={getItemListStyles}
              hidden={hiddenColumnIds.includes(column.id)}
              itemComponent={itemComponent}
              key={column.id}
              onHide={handleHideColumn}
              onHover={handleHoverColumn}
              onLoadMore={onLoadMore}
              onShow={handleShowColumn}
            />
          ))}
          {extraRight}
        </div>
        {loading && (
          <div className={styles.loader}>
            <LoadingSpinner global />
          </div>
        )}
      </div>
    </DragDropContext>
  );
};

export default DnDBoard;
